import { Box, Button, Card, Divider, ownerDocument, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetCohort, GetCohortHistory, GetStudentHistory, GetUser } from "../FirebaseQueries";
import { Timestamp } from "firebase/firestore";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { UserHistory } from "../Types";

interface User {
    fullName: string;
    cohortId: string;
    email: string;
    lastActive: Timestamp | undefined;
}
export default function Student() {
    let params = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<User>({
        fullName: "...",
        cohortId: "",
        email: "...",
        lastActive: undefined,
    });
    const [cohortName, setCohortName] = useState<string>("...");
    const [history, setHistory] = useState<UserHistory[]>([]);
    const [cohortHistory, setCohortHistory] = useState<UserHistory[]>([]);

    const columns: GridColDef[] = [
        { field: "id", headerName: "Code", flex: 1 },
        {
            field: "dateTimeCreated",
            headerName: "Date",
            flex: 1,
            valueGetter: (value: Timestamp) => {
                return value.toDate().toDateString();
            },
        },
        {
            field: "scenarioIndex",
            headerName: "Scenario",
            flex: 1,
            valueGetter: (value: number) => {
                return value + 1;
            },
        },
        {
            field: "overallTimeSec",
            headerName: "Time",
            flex: 1,
            valueGetter: (value: number) => {
                if (value <= 0) return "0";
                return `${Math.floor(value / 60)}:${value % 60}`;
            },
        },
        {
            field: "score",
            headerName: "Score (%)",
            flex: 1,
            valueGetter: (value, row) => {
                if (row.correctTasksCount === 0) {
                    return 0;
                } else {
                    return Math.floor((row.correctTasksCount / row.allTasksCount) * 100);
                }
            },
        },
        {
            field: "view",
            headerName: "",
            renderCell: (params: GridRenderCellParams<any, string>) => (
                <Button variant="contained" onClick={() => navigate(`/app/history/${params.id}`)}>
                    View
                </Button>
            ),
        },
    ];

    // function FilteredHistory(): UserHistory[] {
    //     return [];
    // }

    // Get User
    useEffect(() => {
        const userId: string = params.userId !== undefined ? params.userId : "";

        const FetchUser = async () => {
            const snapshot = await GetUser(userId);
            if (snapshot.exists()) {
                let newUser = {
                    fullName: snapshot.data().fullName,
                    cohortId: snapshot.data().cohortId,
                    email: snapshot.data().email,
                    lastActive: snapshot.data().lastActive,
                };
                setUser(newUser);
            }
        };

        if (userId !== "") FetchUser();
    }, []);

    // Get Cohort and Cohort History
    useEffect(() => {
        const FetchCohort = async () => {
            const snapshot = await GetCohort(user.cohortId);
            if (snapshot.exists()) {
                setCohortName(snapshot.data().name);
            } else {
                setCohortName("None");
            }
        };

        const FetchCohortHistory = async () => {
            await GetCohortHistory(user.cohortId).then((data) => {
                setCohortHistory(data);
            });
        };

        if (user.cohortId !== "") {
            FetchCohort();
            FetchCohortHistory();
        } else {
            setCohortName("None");
        }
    }, [user.cohortId]);

    // Get History
    useEffect(() => {
        const userId: string = params.userId !== undefined ? params.userId : "";

        const FetchHistory = async () => {
            const historySnapshot = await GetStudentHistory(userId);
            const value: UserHistory[] = [];
            historySnapshot.forEach((doc) => {
                value.push({
                    id: doc.data().historyId,
                    scenarioIndex: doc.data().scenarioIndex,
                    dateTimeCreated: doc.data().dateTimeCreated,
                    overallTimeSec: doc.data().overallTimeSec,
                    correctTasksCount: doc.data().correctTasksCount,
                    allTasksCount: doc.data().allTasksCount,
                });
            });

            value.sort((a, b) => {
                if (a.dateTimeCreated !== undefined && b.dateTimeCreated !== undefined) {
                    if (a.dateTimeCreated < b.dateTimeCreated) return 1;
                    if (a.dateTimeCreated > b.dateTimeCreated) return -1;
                }
                return 0;
            });

            setHistory(value);
        };

        FetchHistory();
    }, []);

    function AverageScore(givenHistory: UserHistory[]): string {
        if (givenHistory.length === 0) return "0";
        let sum: number = 0;
        givenHistory.forEach((item) => {
            if (item.correctTasksCount !== 0) sum += item.correctTasksCount / item.allTasksCount;
        });
        return Math.floor((sum / givenHistory.length) * 100).toString();
    }

    function AverageScoreScenario(givenHistory: UserHistory[], scenarioIndex: number): string {
        if (scenarioIndex >= 0) {
            return AverageScore(givenHistory.filter((item) => item.scenarioIndex === scenarioIndex));
        } else {
            return "0";
        }
    }

    function AverageTime(givenHistory: UserHistory[]): string {
        if (givenHistory.length === 0) return "0";

        let sum: number = 0;
        givenHistory.forEach((item) => {
            if (item.overallTimeSec >= 0) sum += item.overallTimeSec;
        });
        let value = Math.floor(sum / givenHistory.length);
        return `${Math.floor(value / 60)}:${value % 60}`;
    }

    function AverageTimeScenario(givenHistory: UserHistory[], scenarioIndex: number): string {
        if (scenarioIndex >= 0) {
            return AverageTime(givenHistory.filter((item) => item.scenarioIndex === scenarioIndex));
        } else {
            return "0";
        }
    }

    function DownloadCSVFile() {
        let headers: string = "Name,Date,Scenario,Time,Score\n";

        let rows: string = "";
        history.forEach((item: UserHistory) => {
            let time: string = `${Math.floor(item.overallTimeSec / 60)}:${item.overallTimeSec % 60}`;
            let score: string = Math.floor((item.correctTasksCount / item.allTasksCount) * 100).toString();
            rows += `${user.fullName},${item.dateTimeCreated?.toDate()},${item.scenarioIndex + 1},${time},${score}\n`;
        });

        let csvContent = "data:text/csv;charset=utf-8," + headers + rows;

        var encodedUri = encodeURI(csvContent);
        window.open(encodedUri);
    }

    return (
        <Stack spacing={2}>
            <Stack direction={"row"} spacing={2} sx={{ width: "100%" }}>
                <Card sx={{ width: "100%" }}>
                    <Typography variant="subtitle1" sx={{ textAlign: "left" }}>
                        Student Information
                    </Typography>
                    <Divider />
                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                        <Typography sx={{ textAlign: "left" }}>Name: {user.fullName}</Typography>
                        <Typography sx={{ textAlign: "left" }}>Cohort: {cohortName}</Typography>
                        <Typography sx={{ textAlign: "left" }}>Email: {user.email}</Typography>
                        <Typography sx={{ textAlign: "left" }}>Last Online: {user.lastActive?.toDate().toDateString()}</Typography>
                    </Stack>
                </Card>
                <Card sx={{ width: "100%" }}>
                    <Typography variant="subtitle1" sx={{ textAlign: "left" }}>
                        Individual Stats
                    </Typography>
                    <Divider />
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Overall Average Score: {AverageScore(history)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Overall Average Time: {AverageTime(history)}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Scenario 1 - Average Score: {AverageScoreScenario(history, 0)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Scenario 1 - Average Time: {AverageTimeScenario(history, 0)}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Scenario 2 - Average Score: {AverageScoreScenario(history, 1)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Scenario 2 - Average Time: {AverageTimeScenario(history, 1)}</Typography>
                        </Box>
                    </Stack>
                </Card>
                <Card sx={{ width: "100%" }}>
                    <Typography variant="subtitle1" sx={{ textAlign: "left" }}>
                        Cohort Stats
                    </Typography>
                    <Divider />
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Overall Average Score: {AverageScore(cohortHistory)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Overall Average Time: {AverageTime(cohortHistory)}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Scenario 1 - Average Score: {AverageScoreScenario(cohortHistory, 0)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Scenario 1 - Average Time: {AverageTimeScenario(cohortHistory, 0)}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ textAlign: "left" }}>Scenario 2 - Average Score: {AverageScoreScenario(cohortHistory, 1)}%</Typography>
                            <Typography sx={{ textAlign: "left" }}>Scenario 2 - Average Time: {AverageTimeScenario(cohortHistory, 1)}</Typography>
                        </Box>
                    </Stack>
                </Card>
            </Stack>
            <Card>
                <Stack direction={'row'} sx={{ justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" sx={{ textAlign: "left" }}>
                        Sessions
                    </Typography>
                    <Button variant="contained" onClick={DownloadCSVFile}>Download CSV</Button>
                </Stack>
                <Paper sx={{ mt: 1 }} variant="outlined">
                    <DataGrid
                        pageSizeOptions={[10, 20]}
                        sx={{ p: 1 }}
                        rows={history}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                    />
                </Paper>
            </Card>
        </Stack>
    );
}
